/**
 * Trains: a locomotive follows the railway (a road line of class `rail`) at a steady speed, turns at the corner of
 * a rail L, halts short of avenue crossings in towns so people can hop on, and reverses at the end of the line.
 * Wagons are vehicles coupled behind it, placed along its breadcrumb trail; the last one is a second locomotive, so at the
 * end of the line (a dwell, then `reverseTrain`) the train goes back the way it came without turning: the head moves to the
 * far end and the wagons keep their places (owner, Sep 7: "trains should be able to go 2 ways — locomotives on both ends").
 * Two trains nose to nose on a single track (a dead end shared by two): after a few seconds one backs off — the one that did
 * not last time, else the higher id — so a pair never stands blocking every crossing (owner: "two trains just stuck").
 * Nothing on the road slows a train down: cars at level crossings wait (ai.ts), people on the track get run over (game.ts
 * runOver), cars in the way get shoved (collide.ts by mass).
 */
import type { WorldStream } from '../engine/world-stream';
import type { RoadHit } from '../world/roads';
import { RF_H, RF_V } from '../world/generator';
import { forward, makeVehicle, type VehicleEntity } from './entities';
import type { Registry } from '../content/registry';
import { Rng } from '../world/rng';

export const TRAIN_SPEED = 18;
const HALT = 6, END_DWELL = 12, HEAD_ON = 3, STUB = 100;
export const COUPLING = 0.5;

function railAt(w: WorldStream, x: number, y: number, axis?: 'v' | 'h'): RoadHit | undefined {
  return w.gen.net.hitsAt(Math.floor(x), Math.floor(y)).find((h) => h.road.class === 'rail' && Math.abs(h.off) <= 1 && (!axis || h.axis === axis));
}
/** The track's centre coordinate for a rail hit (width 3, odd: pos + 0.5). */
const centreOf = (h: RoadHit): number => h.pos + 0.5;

/** Is (x, y) a level crossing of the railway with a level-0 road in a town (where the train halts just before it)? */
function stationCrossing(w: WorldStream, x: number, y: number): string | undefined {
  const hits = w.gen.net.hitsAt(Math.floor(x), Math.floor(y));
  const rail = hits.find((h) => h.road.class === 'rail' && h.off === 0);
  const road = hits.find((h) => h.road.class !== 'rail' && h.level === 0 && Math.abs(h.off) <= Math.floor(h.road.width / 2));
  return rail && road && w.gen.fields.density(x, y) >= 0.36 ? `${road.axis}${road.pos}@${rail.axis}${rail.pos}` : undefined;
}

/** One step of a locomotive. */
export function driveTrain(w: WorldStream, v: VehicleEntity, dt: number, others: VehicleEntity[] = []): void {
  const ai = v.ai!;
  if (v.wrecked) { v.speed = 0; return; }
  w.setMover(v.x, v.y, 0);
  const hit = railAt(w, v.x, v.y, ai.axis);
  if (!hit) { v.speed = Math.max(0, v.speed - 6 * dt); return; } // off the rails somehow: stop
  // stay on the centre line
  if (ai.axis === 'v') v.x = centreOf(hit); else v.y = centreOf(hit);
  v.angle = ai.axis === 'v' ? (ai.dir === -1 ? 0 : Math.PI) : ai.dir === 1 ? Math.PI / 2 : -Math.PI / 2;
  const [fx, fy] = forward(v.angle);
  const nose = v.def.size[1] / 2;
  // halting at a station crossing (a few tiles short of it), once per crossing
  if (ai.stopFor && ai.stopFor > 0) { ai.stopFor = Math.max(0, ai.stopFor - dt); v.speed = Math.max(0, v.speed - 8 * dt); ai.why = 'stop'; }
  else {
    ai.why = '';
    for (let d = Math.ceil(nose) + 1; d <= nose + 4; d++) {
      const px = v.x + fx * d, py = v.y + fy * d;
      const key = stationCrossing(w, px, py);
      if (!key) continue;
      if (ai.lastStop === key) break;
      ai.lastStop = key; ai.stopFor = HALT; break;
    }
  }
  // a level crossing 40–60 m ahead: sound the horn once per crossing
  if (v.speed > 5) for (let d = 40; d <= 60; d += 4) {
    const px = v.x + fx * d, py = v.y + fy * d;
    const f = w.roadFlags(px, py);
    if (!((f & RF_V) && (f & RF_H))) continue;
    const key = `x${Math.floor(px)},${Math.floor(py)}`;
    if (ai.lastHorn !== key) { ai.lastHorn = key; ai.honk = true; }
    break;
  }
  // a junction (the other leg of the line's L crosses here) with only a stub of track ahead (the leg's overshoot past the corner): turn onto the leg that runs on
  {
    const perp: 'v' | 'h' = ai.axis === 'v' ? 'h' : 'v';
    const here = railAt(w, v.x, v.y, perp);
    const key = here ? `j${perp}${here.pos}@${ai.axis}${hit.pos}` : undefined;
    if (here && Math.abs(here.off) <= 1 && key !== ai.lastTurn && stubAhead(w, v, fx, fy, nose)) {
      const on = (dir: 1 | -1, far: number) => { const qx = perp === 'v' ? v.x : v.x + dir * far, qy = perp === 'v' ? v.y + dir * far : v.y; return w.loaded(qx, qy) && !!railAt(w, qx, qy, perp); };
      const dirs = ([1, -1] as const).filter((d) => on(d, 6)); const far = dirs.filter((d) => on(d, 100));
      const pick = (far.length ? far : dirs)[0];
      if (pick !== undefined) { ai.axis = perp; ai.dir = pick; ai.lastTurn = key; return; } // (one turn per junction, so it is not sent straight back)
    }
  }
  // the line ahead: carry on, turn at a corner, or reverse at the end
  const aheadX = v.x + fx * (nose + 2), aheadY = v.y + fy * (nose + 2);
  const ahead = railAt(w, aheadX, aheadY, ai.axis);
  w.setMover(aheadX, aheadY, w.deckH(aheadX, aheadY)); // (the path world's viaducts: the track ahead may be on a deck — a train is on its rails whatever their height)
  if (!ahead || !w.roadAt(aheadX, aheadY)) {
    if (!w.loaded(aheadX, aheadY)) { v.speed = Math.max(0, v.speed - 4 * dt); return; } // the world is not streamed that far yet: wait for it
    // a corner: the perpendicular rail runs on from here in one direction
    const perp: 'v' | 'h' = ai.axis === 'v' ? 'h' : 'v';
    const here = railAt(w, v.x, v.y, perp);
    if (here && Math.abs(here.off) === 0) {
      for (const dir of [1, -1] as const) {
        const qx = perp === 'v' ? v.x : v.x + dir * 6, qy = perp === 'v' ? v.y + dir * 6 : v.y;
        if (railAt(w, qx, qy, perp)) { ai.axis = perp; ai.dir = dir; return; }
      }
    }
    if (v.speed > 0.5) { v.speed = Math.max(0, v.speed - 5 * dt); return; }
    ai.dwell = (ai.dwell ?? 0) + dt; ai.why = 'end'; if (ai.dwell < END_DWELL) return; // the end of the line: a dwell, then back the way it came
    ai.dwell = 0; reverseTrain(v, others.filter((o) => o.coupled?.lead === v.id));
    return;
  }
  // another train on the track ahead: hold back (keeps two trains from grinding into each other)
  let blocked = false, blocker: VehicleEntity | undefined;
  for (const o of others) {
    if (o === v || o.def.class !== 'train' || o.coupled?.lead === v.id) continue;
    const proj = (o.x - v.x) * fx + (o.y - v.y) * fy, lat = Math.abs(-(o.x - v.x) * fy + (o.y - v.y) * fx);
    if (proj > 0 && proj < nose + o.def.size[1] / 2 + 22 && lat < 2) { blocked = true; blocker = o; break; }
  }
  // nose to nose with an oncoming train on the same track: one of the two backs off (see the top)
  const lead = blocker ? (blocker.coupled ? others.find((o) => o.id === blocker!.coupled!.lead) : blocker) : undefined;
  if (lead?.ai && lead.ai.axis === ai.axis && lead.ai.dir === -ai.dir && v.speed < 0.5) {
    ai.headOn = (ai.headOn ?? 0) + dt; ai.why = 'head-on';
    if (ai.headOn > HEAD_ON) { const mine = !!ai.yielded, theirs = !!lead.ai.yielded; const me = mine === theirs ? v.id > lead.id : theirs; if (me) { ai.headOn = 0; ai.yielded = true; lead.ai.yielded = false; reverseTrain(v, others.filter((o) => o.coupled?.lead === v.id)); return; } }
  } else ai.headOn = 0;
  const target = (ai.stopFor && ai.stopFor > 0) || blocked ? 0 : TRAIN_SPEED;
  if (v.speed < target) v.speed = Math.min(target, v.speed + 2.5 * dt);
  else if (v.speed > target) v.speed = Math.max(target, v.speed - 8 * dt);
  const step = v.speed * dt;
  v.x += fx * step; v.y += fy * step;
  v.alt = w.deckH(v.x, v.y); // up the viaduct's ramp and over (mapgen: a railway carried over a road)
  // breadcrumbs for the wagons
  const trail = (ai.trail ??= []);
  const last = trail[trail.length - 1];
  if (!last || Math.hypot(v.x - last.x, v.y - last.y) >= 1) { trail.push({ x: v.x, y: v.y, a: v.angle }); if (trail.length > 120) trail.splice(0, trail.length - 120); }
}

/** the track ahead ends within a stub's length (the leg's overshoot past a junction — 64 tiles — or a dead end), and the world is streamed that far */
function stubAhead(w: WorldStream, v: VehicleEntity, fx: number, fy: number, nose: number): boolean {
  const qx = v.x + fx * (nose + STUB), qy = v.y + fy * (nose + STUB);
  return w.loaded(qx, qy) && !railAt(w, qx, qy, v.ai!.axis);
}
/** The train goes back the way it came without turning: the head takes the far end (the second locomotive's place), the wagons keep theirs */
export function reverseTrain(v: VehicleEntity, wagons: VehicleEntity[]): void {
  const ai = v.ai!; ai.dir = -ai.dir as 1 | -1; ai.lastStop = undefined; ai.stopFor = 0; v.speed = 0;
  v.angle = ai.axis === 'v' ? (ai.dir === -1 ? 0 : Math.PI) : ai.dir === 1 ? Math.PI / 2 : -Math.PI / 2;
  if (!wagons.length) { ai.trail = []; return; }
  const pts = [{ x: v.x, y: v.y, a: v.angle }, ...wagons.slice().sort((a, b) => a.coupled!.index - b.coupled!.index).map((wg) => ({ x: wg.x, y: wg.y, a: wg.angle }))];
  const tail = pts[pts.length - 1]; v.x = tail.x; v.y = tail.y;
  ai.trail = pts.slice(0, -1).map((p) => ({ x: p.x, y: p.y, a: v.angle })); // (the crumbs behind the new head: the old head's place first, up to the wagon before the tail)
}

/** Place a train's wagons along the locomotive's trail (or straight behind it before it has moved). */
export function placeWagons(loco: VehicleEntity, wagons: VehicleEntity[], w?: WorldStream): void {
  const trail = loco.ai?.trail ?? [];
  const len = loco.def.size[1];
  for (const wg of wagons) {
    const back = wg.coupled!.index * (len + COUPLING); // metres behind the loco's centre
    // walk back along the trail from the loco
    let px = loco.x, py = loco.y, pa = loco.angle, left = back, found = false;
    for (let i = trail.length - 1; i >= 0 && !found; i--) {
      const t = trail[i];
      const seg = Math.hypot(px - t.x, py - t.y);
      if (seg >= left) { const k = left / (seg || 1); wg.x = px + (t.x - px) * k; wg.y = py + (t.y - py) * k; wg.angle = t.a; found = true; break; }
      left -= seg; px = t.x; py = t.y; pa = t.a;
    }
    if (!found) { const [fx, fy] = forward(pa); wg.x = px - fx * left; wg.y = py - fy * left; wg.angle = pa; }
    if (wg.def.id === loco.def.id) wg.angle += Math.PI; // (the second locomotive at the tail faces out)
    if (w) wg.alt = w.deckH(wg.x, wg.y);
    wg.speed = loco.speed; wg.wrecked = wg.wrecked || loco.wrecked;
  }
}

/** A train (loco + wagons) on the rail through this tile, heading `dir` along `axis`. Returns the vehicles or [] when the track is not free. */
export function makeTrain(reg: Registry, w: WorldStream, x: number, y: number, rng: Rng, home: string, others: VehicleEntity[]): VehicleEntity[] {
  const hit = railAt(w, x, y); if (!hit || hit.off !== 0) return [];
  const loco = reg.get('vehicle', 'locomotive'); if (!loco) return [];
  const pass = reg.get('vehicle', 'rail_wagon'), freight = reg.get('vehicle', 'freight_wagon');
  const axis = hit.axis, dir: 1 | -1 = rng.chance(0.5) ? 1 : -1;
  const cx = axis === 'v' ? centreOf(hit) : Math.floor(x) + 0.5, cy = axis === 'v' ? Math.floor(y) + 0.5 : centreOf(hit);
  const angle = axis === 'v' ? (dir === -1 ? 0 : Math.PI) : dir === 1 ? Math.PI / 2 : -Math.PI / 2;
  const n = rng.int(2, 4), len = loco.size[1] + COUPLING;
  if (others.some((o) => o.def.class === 'train' && Math.hypot(o.x - cx, o.y - cy) < len * (n + 2))) return [];
  const [fx, fy] = forward(angle);
  for (let i = 0; i <= n; i++) { const qx = cx - fx * len * i, qy = cy - fy * len * i; if (!w.loaded(qx, qy) || !railAt(w, qx, qy, axis)) return []; } // the whole train on straight, streamed track
  const head = makeVehicle(loco, cx, cy, angle, home);
  head.ai = { axis, dir, wait: 0, lost: 0, blockedFor: 0, overtake: 0, stuck: 0, reverse: 0, ignorePeds: 0, redFor: 0 };
  head.speed = TRAIN_SPEED * 0.5;
  const out = [head];
  const kind = rng.chance(0.5) && freight ? freight : pass;
  for (let i = 1; i <= n; i++) { const wdef = i === n ? loco : kind ?? pass; if (!wdef) break; const wg = makeVehicle(wdef, cx - fx * len * i, cy - fy * len * i, angle, home); wg.coupled = { lead: head.id, index: i }; wg.speed = head.speed; out.push(wg); }
  return out;
}

/** Is a train about to pass this level crossing? (a locomotive or wagon within `r` of it, on the track's axis) */
export function trainNear(vehicles: VehicleEntity[], x: number, y: number, r = 45): boolean {
  for (const v of vehicles) { if (v.def.class !== 'train' || v.wrecked) continue; const d = Math.hypot(v.x - x, v.y - y); if (Math.abs(v.speed) > 0.5 ? d < r : d < ON_CROSSING) return true; } // (a standing train that close is on the crossing)
  return false;
}
/** a train standing this close to a crossing is on it (a wagon's half length and the ballast): the barriers stay down, the cars wait */
export const ON_CROSSING = 12;
